import { useEffect, useMemo, useRef } from 'react'
import { useThree, useFrame } from '@react-three/fiber'
import { buildReflectionBlendMaterial, blendTargetOptions, MAX_BLEND } from '../lib/reflectionBlendMaterial.js'
import { createPMREMGenerator } from '../lib/pmrem.js'
import * as THREE from 'three'
import { reflectionManifestUrl, captureUrlFrom } from '../lib/reflectionCaptureManifest'
import { fetchWithRetry } from '../lib/fetchRetry'
import { applyEnvToAll, clearEnvFromAll, setEnvIntensityOnAll } from '../lib/envMapApply'
import { useLiveTune } from '../hooks/useLiveTune'

/**
 * ReflectionCaptureVolume — position-aware specular IBL for splat environments
 * (ENG-561 Phase B). The UE "Reflection Capture" analogue to Phase A's diffuse
 * LightProbeVolume.
 *
 * Loads N pre-baked equirect reflection captures + a manifest, then each frame
 * weights every capture by Unreal Engine's influence-radius DistanceAlpha fade
 * (full inside the inner 60% of its baked `radius`, smoothstepping to 0 at the
 * edge; out-of-range captures contribute nothing — see unrealDistanceAlpha). The
 * MAX_BLEND captures with the largest fade are combined by a normalized
 * DistanceAlpha weighted-sum. Only when the kept set changes or a weight moves
 * past a small threshold does it GPU-blend the SOURCE equirects in one fullscreen
 * pass, PMREM-prefilter the result, and feed the single combined envMap through
 * the shared applier. A stationary avatar prefilters exactly once.
 *
 * Why blend the equirects instead of per-fragment in-shader (true UE): we light
 * ONE avatar at ONE sample position, so blending radiance at that point and
 * handing materials one envMap is equivalent to the per-fragment blend, without
 * maintaining MAX_BLEND-sample shader patches on the hot path. (The composite is
 * a normalized DistanceAlpha sum rather than UE's order-dependent "under"
 * operator — see `reflectionBlendMaterial.js` for why that fits the
 * single-point/auto-grid case.)
 *
 * Off the React hot path: textures, render target, PMREM, scratch all live in
 * refs; useFrame mutates with no allocation and no React state. Renders nothing.
 *
 * No silent fallback (CLAUDE.md): a missing manifest surfaces as
 * window.__REFLECTION_CAPTURES__.state === 'no-cache' and EnvProbe's legacy
 * single-equirect path (real baked data) stays in charge — never faked.
 */

const EQUIRECT_W = 2048
const EQUIRECT_H = 1024
const WEIGHT_EPS = 0.02 // re-prefilter only when a blend weight moves this far
// Unreal Engine's reflection-capture influence fade. Given norm = dist / radius
// (capture skipped entirely once norm >= 1), this is 1.0 across the inner 60% of
// the radius and smoothsteps to exactly 0.0 at the radius edge. The zero-at-edge
// is what makes capture transitions pop-free. Verbatim from the UE source:
//   Engine/Shaders/Private/ReflectionEnvironmentShared.ush:197-198
//     float x = saturate(2.5 * NormalizedDistanceToCapture - 1.5);
//     DistanceAlpha = 1 - x*x*(3 - 2*x);
function unrealDistanceAlpha(norm) {
  if (norm >= 1) return 0
  const x = Math.min(1, Math.max(0, 2.5 * norm - 1.5))
  return 1 - x * x * (3 - 2 * x)
}

export default function ReflectionCaptureVolume({ splatUrl, splatVersion, reloadKey = 0 }) {
  const gl    = useThree(s => s.gl)
  const scene = useThree(s => s.scene)
  const tune  = useLiveTune()
  const { iblEnabled, envMapIntensity } = tune.lighting

  // All hot-path state in refs — never React state.
  const capsRef     = useRef(null)   // [{ p:[x,y,z], radius }]
  const texRef      = useRef(null)   // THREE.Texture[] source equirects
  const blendRTRef  = useRef(null)   // WebGLRenderTarget (sRGB equirect)
  const blendMatRef = useRef(null)   // the blend handle (material + setInputs)
  // Reused every pass — the useFrame path allocates nothing.
  const blendTex = useMemo(() => new Array(MAX_BLEND).fill(null), [])
  const blendW = useMemo(() => new Array(MAX_BLEND).fill(0), [])
  const blendSceneRef = useRef(null) // ortho fullscreen scene
  const blendCamRef = useRef(null)
  const pmremRef    = useRef(null)   // PMREMGenerator
  const envRTRef    = useRef(null)   // current PMREM render target
  const tmpVec      = useRef(new THREE.Vector3())
  // Selection scratch (fixed-size, mutated each frame — no per-frame alloc).
  const selIdxRef    = useRef(new Int32Array(MAX_BLEND))   // kept capture indices
  const selARef      = useRef(new Float64Array(MAX_BLEND)) // their DistanceAlpha
  const selWRef      = useRef(new Float64Array(MAX_BLEND)) // normalized weights
  const lastIdxRef   = useRef(new Int32Array(MAX_BLEND).fill(-1))
  const lastWArrRef  = useRef(new Float64Array(MAX_BLEND).fill(-1))
  const intensityRef = useRef(envMapIntensity)
  const readyRef    = useRef(false)

  // Keep the live-slider value available to the no-realloc useFrame path.
  useEffect(() => { intensityRef.current = envMapIntensity }, [envMapIntensity])

  useEffect(() => {
    let cancelled = false
    const setState = (state, extra) => {
      if (typeof window !== 'undefined') window.__REFLECTION_CAPTURES__ = { state, ...extra }
    }

    readyRef.current = false
    lastIdxRef.current.fill(-1)
    lastWArrRef.current.fill(-1)

    if (!splatUrl || !iblEnabled) { setState(iblEnabled ? 'idle' : 'disabled'); return }
    setState('pending')

    ;(async () => {
      // ?v=<splatVersion> busts the proxy/browser cache after a (re)bake.
      const bust = splatVersion ? `?v=${encodeURIComponent(splatVersion)}` : ''
      const servedManifest = await reflectionManifestUrl(splatUrl)
      if (cancelled) return
      // null = the server lists no reflection manifest for this environment, so
      // there is nothing to fetch. Only a URL we were given gets requested
      // (ENG-999): the old code computed one, and a computed path that 404s is
      // indistinguishable from an environment that was never baked.
      if (!servedManifest) {
        console.warn(`[ReflectionCaptureVolume] no reflection manifest for ${splatUrl} (server lists none). Bake with: npm run bake-reflections -- ${splatUrl}`)
        setState('no-cache', { url: null })
        return
      }
      const manifestUrl = servedManifest + bust
      // GET, not HEAD: the asset-manager env-file proxy is a GET-only route, so
      // HEAD 404s (would false-report no-cache). A missing manifest is a real
      // 404; a SPA-fallback (bundled mode) is text/html → JSON parse fails below.
      const res = await fetchWithRetry(manifestUrl, { cache: 'no-cache' }).catch(() => null) // retry transient 5xx, not 404; revalidate via ETag so a server-side re-bake shows on plain reload
      if (cancelled) return
      if (!res || !res.ok) {
        console.warn(`[ReflectionCaptureVolume] no reflection manifest at ${manifestUrl}. Bake with: npm run bake-reflections -- ${splatUrl}`)
        setState('no-cache', { url: manifestUrl })
        return
      }
      // Read text first so a parse failure carries the evidence: an HTML head
      // = SPA-fallback / proxy error page (routing problem); a JSON head with
      // a short length = body truncated in flight (connection dropped
      // mid-stream under load). A bare "non-JSON body" can't separate them.
      const bodyText = await res.text()
      let manifest
      try { manifest = JSON.parse(bodyText) } catch {
        console.warn(`[ReflectionCaptureVolume] no reflection manifest at ${manifestUrl} (200 but unparseable body: len=${bodyText.length} head=${JSON.stringify(bodyText.slice(0, 60))}). Bake with: npm run bake-reflections -- ${splatUrl}`)
        setState('no-cache', { url: manifestUrl, bodyLen: bodyText.length, bodyHead: bodyText.slice(0, 60) })
        return
      }
      if (cancelled) return
      const captures = manifest.captures ?? []
      if (!captures.length) { setState('error', { error: 'manifest has no captures' }) ; return }

      // Load every source equirect (loud-fail: TextureLoader rejects on 404).
      // Report loaded/total as each lands so the status chip can show an
      // explicit "loading reflections N/M" instead of a silent black ball while
      // the (large) captures stream in.
      const loader = new THREE.TextureLoader()
      const total = captures.length
      let loaded = 0
      setState('pending', { loaded, total })
      // Each capture sits beside THE manifest we just parsed, resolved
      // relative to its URL — so the images can only come from the same place
      // the capture list did, and nothing here re-derives a path.
      const textures = await Promise.all(captures.map((_, i) => {
        const u = captureUrlFrom(servedManifest, i)
        return new Promise((resolve, reject) => {
          loader.load(u + bust, (tex) => {
            loaded++
            if (!cancelled) setState('pending', { loaded, total })
            resolve(tex)
          }, undefined, () => reject(new Error(`reflection capture ${u} failed to load`)))
        })
      }))
      if (cancelled) { textures.forEach(t => t.dispose()); return }
      for (const t of textures) {
        t.generateMipmaps = false
        t.minFilter = THREE.LinearFilter
        t.magFilter = THREE.LinearFilter
        t.wrapS = THREE.RepeatWrapping  // equirect longitude wraps
        t.wrapT = THREE.ClampToEdgeWrapping
        t.needsUpdate = true
      }

      // Build the blend pipeline once.
      // LINEAR AND HALF-FLOAT, not 8-bit sRGB. This target exists only to be
      // read by `PMREMGenerator`, which wants linear radiance — so encoding to
      // sRGB on the way in and decoding on the way out was a round trip that
      // bought nothing and cost two things.
      //
      // It cost PRECISION: 8 bits for an environment that is then blurred into a
      // mip chain. And it cost CORRECTNESS across renderers, which is what made
      // it worth changing now. An 8-bit target TAGGED `SRGBColorSpace` leaves who
      // applies the transfer curve up to the backend — the hardware on write, or
      // the sampler on read — and the two paths did not agree. Measured on myra:
      // with the blend removed and a synthetic grey environment in its place,
      // PMREM and the shading agree to 4.2 levels across a 7-step sweep, while
      // the real scene differed by 24-32 levels on the same patches. The
      // difference was in this texture, not in anything downstream of it.
      //
      // Linear float has no such ambiguity: the shader writes radiance, PMREM
      // reads radiance, and nothing in between is entitled to an opinion.
      //
      // The claim above was re-measured rather than believed, against the one
      // thing that can settle it: the capture PNG itself. At ONE capture and
      // weight 1 the blend's whole job is to reproduce that image's radiance, so
      // a PMREM taken straight from the PNG is ground truth — and this target
      // matches it exactly (84.0 on a grey sphere) where the 8-bit sRGB one
      // reads 100.4. The format and the shader's output encoding are ONE
      // decision and are resolved together in `reflectionBlendMaterial.js`.
      const blendRT = new THREE.WebGLRenderTarget(EQUIRECT_W, EQUIRECT_H,
        blendTargetOptions(THREE))
      blendRT.texture.mapping = THREE.EquirectangularReflectionMapping
      // Whichever renderer is mounted. A raw-GLSL ShaderMaterial does not merely
      // fail to draw under a node renderer — it throws mid-frame and wedges it
      // for every later render, and this component mounts unconditionally.
      //
      // The RENDERER is handed over, not just "is this a node renderer": the two
      // graphics APIs disagree about which end of a render target NDC y=+1 is,
      // and getting that wrong mirrors the whole environment vertically with
      // nothing on screen to say so. See `flipsRenderTargetV`.
      const blend = await buildReflectionBlendMaterial({ renderer: gl })
      if (cancelled) { blend.dispose(); return }
      const blendScene = new THREE.Scene()
      blendScene.add(new THREE.Mesh(new THREE.PlaneGeometry(2, 2), blend.material))
      const blendCam = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1)
      // The generator for the renderer that is MOUNTED — three's core one
      // prefilters with raw ShaderMaterials and silently yields an empty cube on
      // a node renderer (src/lib/pmrem.js).
      const pmrem = await createPMREMGenerator(gl)
      pmrem.compileEquirectangularShader()
      if (cancelled) { pmrem.dispose(); blend.dispose(); return }

      capsRef.current = captures.map(c => ({ p: c.p, radius: c.radius ?? Infinity }))
      texRef.current = textures
      blendRTRef.current = blendRT
      blendMatRef.current = blend
      blendSceneRef.current = blendScene
      blendCamRef.current = blendCam
      pmremRef.current = pmrem
      readyRef.current = true
      setState('applied', { count: captures.length, activePair: [0, 0], weight: 0, envMapUuid: null })
      console.log(`[ReflectionCaptureVolume] loaded ${captures.length} reflection captures for ${splatUrl}`)
    })().catch(err => {
      if (cancelled) return
      console.error('[ReflectionCaptureVolume] failed:', err)
      setState('error', { error: String(err?.message ?? err) })
    })

    return () => {
      cancelled = true
      readyRef.current = false
      if (envRTRef.current) {
        clearEnvFromAll(envRTRef.current.texture)
        if (scene.environment === envRTRef.current.texture) scene.environment = null
        envRTRef.current.dispose()
        envRTRef.current = null
      }
      texRef.current?.forEach(t => t.dispose())
      blendRTRef.current?.dispose()
      blendMatRef.current?.dispose()
      pmremRef.current?.dispose()
      capsRef.current = texRef.current = blendRTRef.current = null
      blendMatRef.current = blendSceneRef.current = blendCamRef.current = pmremRef.current = null
      if (typeof window !== 'undefined' && window.__REFLECTION_CAPTURES__?.state !== 'no-cache') {
        delete window.__REFLECTION_CAPTURES__
      }
    }
    // reloadKey bumps when a bake for this env completes (bakeReload.js) → re-run
    // so freshly-baked captures load without a manual env re-select. splatVersion
    // still re-runs it on a splat re-upload.
  }, [splatUrl, splatVersion, iblEnabled, reloadKey]) // eslint-disable-line react-hooks/exhaustive-deps

  // Live-mutate envMapIntensity without re-prefiltering (mirrors EnvProbe).
  useEffect(() => {
    if (!readyRef.current) return
    setEnvIntensityOnAll(envMapIntensity)
    if (typeof window !== 'undefined' && window.__REFLECTION_CAPTURES__?.state === 'applied') {
      window.__REFLECTION_CAPTURES__.intensity = envMapIntensity
    }
  }, [envMapIntensity])

  useFrame(() => {
    if (!readyRef.current) return
    const caps = capsRef.current
    if (!caps || caps.length === 0) return

    // Sample point: __AVATAR_PROBE_POS__ override (tests + dev oracle), else the
    // avatar's world position raised to body height (matches LightProbeVolume).
    const override = (typeof window !== 'undefined' && window.__AVATAR_PROBE_POS__) || null
    let x, y, z
    if (override) {
      x = override[0]; y = override[1]; z = override[2]
    } else {
      const root = (typeof window !== 'undefined' && window.__AVATAR_SCENE__) || null
      if (!root) return
      root.getWorldPosition(tmpVec.current)
      x = tmpVec.current.x; y = tmpVec.current.y + 1.2; z = tmpVec.current.z
    }

    // Single pass: track the two nearest captures (by distance, for reporting)
    // and keep the MAX_BLEND captures with the largest UE DistanceAlpha (for the
    // blend). No sort, no alloc — fixed scratch buffers.
    const idx = selIdxRef.current, av = selARef.current, wv = selWRef.current
    let ni = -1, mi = -1, nd = Infinity, md = Infinity
    let n = 0, sumA = 0
    for (let i = 0; i < caps.length; i++) {
      const p = caps[i].p
      const dx = p[0] - x, dy = p[1] - y, dz = p[2] - z
      const d = Math.sqrt(dx * dx + dy * dy + dz * dz)
      if (d < nd) { mi = ni; md = nd; ni = i; nd = d }
      else if (d < md) { mi = i; md = d }

      const r = caps[i].radius
      const a = unrealDistanceAlpha(r > 0 ? d / r : 0) // r=Infinity → norm 0 → a 1
      if (a <= 0) continue
      if (n < MAX_BLEND) { idx[n] = i; av[n] = a; n++ }
      else {
        let mink = 0
        for (let k = 1; k < MAX_BLEND; k++) if (av[k] < av[mink]) mink = k
        if (a > av[mink]) { idx[mink] = i; av[mink] = a }
      }
    }
    if (mi < 0) { mi = ni } // single capture total

    // Normalize DistanceAlpha into blend weights. Fallback: at the grid's outer
    // fringe no capture is in influence range (sumA 0) — keep the single nearest
    // so the reflection is never empty/black.
    for (let k = 0; k < n; k++) sumA += av[k]
    if (sumA > 0) { for (let k = 0; k < n; k++) wv[k] = av[k] / sumA }
    else { idx[0] = ni; wv[0] = 1; n = 1 }

    // Throttle: re-prefilter only when the kept set changes or a weight moves
    // past WEIGHT_EPS. Stationary avatar → prefilters exactly once.
    const lastIdx = lastIdxRef.current, lastW = lastWArrRef.current
    let changed = false
    for (let k = 0; k < MAX_BLEND; k++) {
      const ci = k < n ? idx[k] : -1
      if (ci !== lastIdx[k]) { changed = true; break }
    }
    let maxDW = 0
    for (let k = 0; k < n; k++) { const dw = Math.abs(wv[k] - lastW[k]); if (dw > maxDW) maxDW = dw }
    if (!changed && maxDW <= WEIGHT_EPS) return

    // Crossing: weighted-sum the kept source equirects, prefilter, apply.
    // Through the blend's own setter: the two backends keep these inputs in
    // different places (a uniform array against per-slot nodes), and a caller
    // reaching into `uniforms` would have to know which renderer it was on.
    for (let k = 0; k < MAX_BLEND; k++) {
      if (k < n) { blendTex[k] = texRef.current[idx[k]]; blendW[k] = wv[k] }
      else       { blendTex[k] = texRef.current[idx[0]]; blendW[k] = 0 }
    }
    blendMatRef.current.setInputs(blendTex, blendW)
    const gl_ = gl
    const prevRT = gl_.getRenderTarget()
    gl_.setRenderTarget(blendRTRef.current)
    gl_.render(blendSceneRef.current, blendCamRef.current)
    gl_.setRenderTarget(prevRT)

    const envRT = pmremRef.current.fromEquirectangular(blendRTRef.current.texture)
    if (envRTRef.current) envRTRef.current.dispose()
    envRTRef.current = envRT
    scene.environment = envRT.texture
    applyEnvToAll(envRT.texture, intensityRef.current)

    for (let k = 0; k < MAX_BLEND; k++) {
      lastIdx[k] = k < n ? idx[k] : -1
      lastW[k] = k < n ? wv[k] : 0
    }
    if (typeof window !== 'undefined' && window.__REFLECTION_CAPTURES__) {
      const g = window.__REFLECTION_CAPTURES__
      // Back-compat reporting: the two nearest captures (sorted by index) and the
      // higher-index member's normalized share — 0 (fully lo) → 1 (fully hi).
      const wOf = (c) => { for (let k = 0; k < n; k++) if (idx[k] === c) return wv[k]; return 0 }
      const lo = Math.min(ni, mi), hi = Math.max(ni, mi)
      const wLo = wOf(lo), wHi = wOf(hi), denom = wLo + wHi
      g.state = 'applied'
      g.activePair = [lo, hi]
      g.weight = (lo !== hi && denom > 0) ? wHi / denom : 0
      g.count = caps.length
      g.activeIndices = Array.from(idx.subarray(0, n))
      g.weights = Array.from(wv.subarray(0, n))
      g.envMapUuid = envRT.texture.uuid // changes on every real re-prefilter (test oracle)
    }
  })

  return null
}
